import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { Domain } from "~/api/Api.ts";
import { apiClient } from "~/api/ApiClient.ts";
import Button from "~/components/mantine/button/Button.tsx";
import Select from "~/components/mantine/select/Select.tsx";
import { useSites } from "~/components/siteSpeed/hooks.ts";
import { useVisibilityEngineRoutes } from "~/components/siteSpeed/useVisibilityEngineRoutes";
import ButtonWithSpinner from "~/components/ui/button/Button.tsx";
import { AlertCard } from "~/components/ui/card/AlertCard";
import CopyButton from "~/components/ui/copyButton/CopyButton.tsx";
import Input from "~/components/ui/input/Input.tsx";
import Spinner from "~/components/ui/spinner/Spinner.tsx";
import { useWizard } from "~/contexts/WizardContext";

import styles from "./Wizzard.module.scss";

type ConfigureProps = {
  isWizzard?: boolean;
  domain?: Domain | undefined;
};

export const Configure = ({ isWizzard = false }: ConfigureProps) => {
  const [siteId, setSiteId] = useState<number>();
  const [isChecking, setIsChecking] = useState(false);
  const { domainId } = useParams<{ domainId: string }>();
  const navigate = useNavigate();
  const veRoutes = useVisibilityEngineRoutes();
  const { navigateToPrevStep } = useWizard();

  const { data: sitesData, isPending: isPendingSitesData } = useSites(
    Number(domainId),
  );

  const { data: domainStatus } = useQuery({
    enabled: isChecking,
    queryKey: ["domainStatus", domainId, "configure"],
    queryFn: async () => {
      const response = await apiClient.domains.statusDetail(Number(domainId));

      return response?.data?.domain;
    },
    refetchOnWindowFocus: false,
    refetchInterval: isChecking ? 10000 : false,
    refetchOnMount: true,
  });

  useEffect(() => {
    if (isChecking && domainStatus?.status?.configure && domainId) {
      setIsChecking(false);
      navigate(veRoutes.domain(domainId));
    }
  }, [isChecking, domainStatus, domainId, navigate, veRoutes]);

  const handleCheckInstallation = () => {
    setIsChecking(true);
  };

  const sites =
    sitesData?.sites &&
    sitesData.sites.filter((site) => site.prerendering_status === "enabled");

  const site = sites?.find((s) => s.id === siteId);
  const token = site?.proxy_token ?? "";

  const handleSiteSelect = (id: number) => {
    setSiteId(id);
  };

  useEffect(() => {
    if (sites?.length === 1) {
      setSiteId(sites[0].id);
    }
  }, [sites]);

  if (isPendingSitesData) {
    return <Spinner />;
  }

  return (
    // @ts-expect-error TS2339 non-existing className
    <div className={styles.stepContent}>
      <div>
        <div className={styles.stepContentTitle}>
          Configure the AI Visibility Engine plugin
        </div>
        <div>
          Paste your Access Token into the plugin settings and enable it.
        </div>

        {sites && sites.length > 0 ? (
          <>
            {sites.length > 1 && (
              <div className={styles.sitesSelector}>
                <Select
                  data={sites.map((s) => ({
                    label: s.name,
                    value: String(s.id),
                  }))}
                  placeholder="Select Site"
                  isLoading={false}
                  onChange={(id) => handleSiteSelect(Number(id))}
                />
              </div>
            )}

            {siteId && (
              <>
                <AlertCard
                  neutralHeader={false}
                  className={styles.accessTokenInfoBlock}
                  type="info"
                  title="Your Access Token"
                  content={
                    <div>
                      <div>
                        This token authenticates requests between the WordPress
                        plugin and the AI Visibility Engine. Keep it secure and
                        never expose it publicly.
                      </div>
                      <div
                        className={clsx(
                          styles.stepContentCodeInput,
                          styles.accessTokenValue,
                        )}
                      >
                        <Input
                          readOnly
                          isSecret
                          value={token}
                          ownProps={{ noErrors: true }}
                          wrapperClassName={styles.fullWidthInput}
                        />
                        <CopyButton value={token} />
                      </div>
                    </div>
                  }
                />

                <div className={styles.edgeInstallationDetailsWrapper}>
                  <div className={styles.subTitle}>Installation Steps</div>
                  <div className={styles.edgeInstallationDetailsContent}>
                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>1</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Open plugin settings</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          In WordPress admin, go to{" "}
                          <strong>Settings → AI Visibility Engine</strong>.
                        </div>
                      </div>
                    </div>

                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>2</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Paste your Access Token</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          In the <strong>Access Token</strong> section, paste
                          the token above into the <strong>Token</strong> field.
                        </div>
                      </div>
                    </div>

                    <div className={styles.edgeInstallationDetailsStep}>
                      <div className={styles.stepNumber}>3</div>
                      <div
                        className={styles.edgeInstallationDetailsStepContent}
                      >
                        <strong>Enable the engine</strong>
                        <div
                          className={
                            styles.edgeInstallationDetailsStepContentDescription
                          }
                        >
                          Toggle <strong>Status</strong> to{" "}
                          <strong>Enabled</strong> and save the settings.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.edgeInstallationDetailsWrapper}>
                  <div className={styles.subTitle}>How it works</div>
                  <div className={styles.edgeInstallationDetailsContent}>
                    <ul className={styles.edgeInstallationDetailsList}>
                      <li>
                        The plugin intercepts requests from search engine bots
                        (detected via User-Agent) before WordPress renders the
                        page.
                      </li>
                      <li>
                        Bot requests are forwarded to the AI Visibility Engine
                        for optimization.
                      </li>
                      <li>
                        Regular user traffic continues to WordPress unchanged.
                      </li>
                      <li>
                        No DNS changes required — everything runs inside your
                        WordPress install.
                      </li>
                    </ul>
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <div className={styles.edgeInstallationNoSites}>
            You have no sites with enabled prerendering. Go to AI Visibility
            Engine Status section and enable one.
          </div>
        )}
      </div>
      {isWizzard && (
        <div className={styles.stepContentFooter}>
          <Button variant="outlined" onClick={() => navigateToPrevStep()}>
            Back
          </Button>
          <ButtonWithSpinner
            type="button"
            onClick={handleCheckInstallation}
            disabled={isChecking}
            withSpinner={isChecking}
          >
            {isChecking ? "Waiting for Requests" : "Check Installation"}
          </ButtonWithSpinner>
        </div>
      )}
    </div>
  );
};
